import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicyPage.css';

const EFFECTIVE_DATE = 'February 10, 2025';
const CANONICAL_URL = 'https://portalrapp.com/privacy';

/**
 * Privacy Policy page - Public page, no authentication required.
 * Required for Google and Microsoft OAuth configuration.
 */
export const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Portalr Privacy Policy';

    const metaDescription = document.querySelector('meta[name="description"]');
    const description =
      'Portalr Privacy Policy. Learn how we collect, use, and protect your information including OAuth integrations with Google and Microsoft, payment processing via Stripe, and multi-tenant data isolation.';
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = description;
      document.head.appendChild(meta);
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = CANONICAL_URL;

    return () => {
      document.title = 'Portalr';
    };
  }, []);

  return (
    <div className="privacy-policy-page">
      <div className="privacy-policy-container">
        <header className="privacy-policy-header">
          <h1>Portalr Privacy Policy</h1>
          <p className="effective-date">Effective Date: {EFFECTIVE_DATE}</p>
        </header>

        <main className="privacy-policy-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Portalr ("we", "our", or "us") provides cloud-based software solutions including
              Portalr Core and Portalr Tax for professional service organizations. This Privacy
              Policy explains how we collect, use, disclose, and protect information when you use
              our services.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <p>We may collect the following categories of information:</p>
            <h3>Account Information</h3>
            <ul>
              <li>Organization name</li>
              <li>User names</li>
              <li>Email addresses</li>
              <li>Contact information</li>
            </ul>
            <h3>Client Data</h3>
            <ul>
              <li>Documents uploaded by tenant organizations</li>
              <li>Invoices and billing data</li>
              <li>Tax-related documents and metadata (Portalr Tax)</li>
            </ul>
            <h3>Usage Data</h3>
            <ul>
              <li>Log data</li>
              <li>IP addresses</li>
              <li>Browser type</li>
              <li>Interaction events</li>
            </ul>
            <h3>Payment Information</h3>
            <p>
              Portalr does not store credit card information. Payment processing is handled by
              third-party providers such as Stripe.
            </p>
          </section>

          <section>
            <h2>3. How We Use Information</h2>
            <p>We use collected information to:</p>
            <ul>
              <li>Provide and maintain the Portalr platform</li>
              <li>Enable secure document sharing</li>
              <li>Facilitate invoicing and payment workflows</li>
              <li>Send transactional communications</li>
              <li>Improve system performance and security</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>We do not sell personal information.</p>
          </section>

          <section>
            <h2>4. Email Integrations (Google and Microsoft)</h2>
            <p>
              Portalr allows tenant organizations to connect their Google or Microsoft email
              accounts using OAuth 2.0 authorization.
            </p>
            <p>When a tenant connects an email account:</p>
            <ul>
              <li>Portalr requests permission only to send email on the tenant's behalf.</li>
              <li>Portalr does not read inbox contents.</li>
              <li>Portalr does not access or store full mailbox data.</li>
              <li>OAuth access tokens are encrypted at rest.</li>
              <li>Tokens are used solely for sending authorized communications.</li>
              <li>Access may be revoked by the tenant at any time.</li>
            </ul>
            <p>
              Portalr's use of information received from Google APIs adheres to the Google API
              Services User Data Policy, including the Limited Use requirements.
            </p>
          </section>

          <section>
            <h2>5. Payment Processing</h2>
            <p>Portalr integrates with third-party payment providers such as Stripe.</p>
            <ul>
              <li>Portalr does not store full credit card numbers.</li>
              <li>Payment information is processed directly by the payment provider.</li>
              <li>Payment provider privacy policies govern the processing of financial data.</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Security</h2>
            <p>
              We implement reasonable administrative, technical, and organizational safeguards,
              including:
            </p>
            <ul>
              <li>Encryption in transit (HTTPS/TLS)</li>
              <li>Encryption at rest</li>
              <li>Role-based access control</li>
              <li>Tenant-level data isolation</li>
              <li>Secure token storage for integrations</li>
            </ul>
          </section>

          <section>
            <h2>7. Data Retention</h2>
            <p>We retain data for as long as necessary to:</p>
            <ul>
              <li>Provide services</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes</li>
              <li>Enforce agreements</li>
            </ul>
            <p>
              Tenants may request deletion of their data subject to contractual and legal
              requirements.
            </p>
          </section>

          <section>
            <h2>8. Multi-Tenant Architecture</h2>
            <p>
              Portalr operates as a multi-tenant SaaS platform. Each tenant organization's data is
              logically isolated. Access to data is restricted based on role-based permissions.
            </p>
          </section>

          <section>
            <h2>9. Third-Party Services</h2>
            <p>Portalr may integrate with third-party services including:</p>
            <ul>
              <li>Amazon Web Services</li>
              <li>Stripe</li>
              <li>Google APIs</li>
              <li>Microsoft Graph APIs</li>
            </ul>
            <p>These providers process data according to their own privacy policies.</p>
          </section>

          <section>
            <h2>10. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have rights to:</p>
            <ul>
              <li>Access your information</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion</li>
              <li>Restrict processing</li>
            </ul>
            <p>Requests may be directed to the contact information below.</p>
          </section>

          <section>
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically. Changes will be posted at this URL
              with an updated effective date.
            </p>
          </section>

          <section>
            <h2>12. Contact Information</h2>
            <p>For privacy-related inquiries, contact:</p>
            <p>
              <a href="mailto:support@portalrapp.com">support@portalrapp.com</a>
            </p>
          </section>
        </main>

        <footer className="privacy-policy-footer">
          <Link to="/login">Return to Login</Link>
        </footer>
      </div>
    </div>
  );
};
